import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  Container,
  Divider,
  LinearProgress,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Page } from 'src/components';
import Toolbar from './Toolbar';
import SupplierDetail from './SupplierDetail';
import { Listing } from 'src/types';
import { slices, useAppDispatch } from 'src/redux';
import { isNil } from 'lodash';
import { unwrapResult } from '@reduxjs/toolkit';
import { cleanSN, getStatusColor } from 'src/utils';
import { Pagination } from '@material-ui/lab';
import { Supplier } from 'src/types/supplier';

const { actions: listingActions } = slices.listing;
const { actions: supplierActions } = slices.supplier;

const useStyles = makeStyles(() => ({
  root: {}
}));

const SupplierDetailsView = () => {
  const classes = useStyles();
  const params = useParams();
  const dispatch = useAppDispatch();

  const [supplierDetails, setSupplierDetails] = useState<Supplier | null>(null);
  const [listings, setListings] = useState<Listing[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [lastPage, getLastPage] = useState<number>();
  const [page, setPage] = useState(1);

  const getProductsViaSupplier = async (keyword?: string, pageNum = 1) => {
    if (params?.id) {
      setIsLoading(true);
      const listingPayload = {
        supplier_id: +params?.id || null,
        keyword: cleanSN(keyword ?? ''), // keyword is treated as serial by api
        limit: 10,
        branch_id: 1,
        page: pageNum,
        status: null
      };
      const response = unwrapResult(
        await dispatch(listingActions?.getListingsThunk(listingPayload))
      );

      if (response?.success && response?.originalData?.data) {
        setIsLoading(false);
        setListings(response?.originalData?.data);
        getLastPage(response?.originalData?.meta?.last_page);
      }
    }
  };

  const onSearchProduct = (keyword: string | undefined) => {
    if (!isNil(keyword)) {
      getProductsViaSupplier(keyword);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<any>, value: number) => {
    setPage(value);
    getProductsViaSupplier('', value);
  };

  const getSupplierDetails = async (id: number) => {
    if (id) {
      const res = unwrapResult(
        await dispatch(supplierActions.getSupplierDetailsThunk(id))
      );

      const details = res?.originalData?.data;

      if (res?.success && details) {
        setSupplierDetails(details);
      } else {
        console.error('Failed to get supplier details');
      }
    }
  };

  useEffect(() => {
    if (params?.id) {
      getProductsViaSupplier();
      getSupplierDetails(+params?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.id]);

  return (
    <Page
      className={classes.root}
      title={`Supplier: ${supplierDetails?.name ?? 'N/A'}`}
    >
      <Container maxWidth={false}>
        <SupplierDetail supplier={supplierDetails} />
        <Toolbar onSearchProduct={onSearchProduct} />
        <Box marginY={2}>
          <Card>
            <Divider />
            <PerfectScrollbar>
              <Box>
                {isLoading ? <LinearProgress /> : null}
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product Name</TableCell>
                      <TableCell>Serial No.</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {listings.map((item: Listing) => (
                      <TableRow hover key={item.id}>
                        <TableCell>{item?.product?.name ?? '--'}</TableCell>
                        <TableCell>{item?.serial_no ?? 'jkasjfk'}</TableCell>
                        <TableCell>
                          <Typography
                            style={{ color: getStatusColor(item.status) }}
                          >
                            {item.status}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </PerfectScrollbar>
          </Card>
        </Box>
        <Box display="flex" justifyContent="flex-end" p={2}>
          <Pagination
            count={lastPage}
            page={page}
            onChange={handlePageChange}
          />
        </Box>
      </Container>
    </Page>
  );
};

export default SupplierDetailsView;
